import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SendLiveButton } from "@/lib/notifications";
import AdvisorQuestionnaireModal from "@/components/AdvisorQuestionnaireModal";
import ServicePlanQuoteModal from "@/components/ServicePlanQuoteModal";
import {
  ArrowLeft, Calendar, Clock, Phone, Mail, MessageSquare, FileText, Upload, Camera, Printer,
  Sparkles, Loader2, Check, ExternalLink, Stethoscope, Send, Copy, X, ChevronDown, ChevronUp,
  AlertTriangle, CheckSquare, ListChecks,
} from "lucide-react";
import { BrandLink } from "@/components/Brand";
import DiaryImportWizard from "@/components/DiaryImportWizard";
import QuickPreCallModal from "@/components/QuickPreCallModal";

const SVC_COLOUR = {
  diagnostic: "bg-warning/15 text-warning border-warning/40",
  service: "bg-primary/15 text-primary border-primary/40",
  mot: "bg-success/15 text-success border-success/40",
  repair: "bg-destructive/15 text-destructive border-destructive/40",
  inspection: "bg-muted/40 text-muted-foreground border-border",
  other: "bg-muted/40 text-muted-foreground border-border",
};

function CopyBtn({ text, label, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" onClick={async () => {
      try { await navigator.clipboard.writeText(text || ""); setDone(true); toast.success(`${label} copied`); setTimeout(() => setDone(false), 1500); }
      catch { toast.error("Couldn't copy"); }
    }} className="btn-outline text-xs" data-testid={testid}>
      {done ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

// =====================================================================
// One pre-call row — expandable to show preview + actions
// =====================================================================
// =====================================================================
// Pre-call advisor checklist — Toyota service standard, 7 items
// =====================================================================
function PreCallChecklistPanel({ jobId, checklist, busy, onToggle, onNotes, onComplete }) {
  const [notesDraft, setNotesDraft] = useState(null);
  if (!checklist) {
    return (
      <div className="py-6 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading checklist…
      </div>
    );
  }
  const allDone = checklist.done === checklist.total;
  const notesValue = notesDraft != null ? notesDraft : (checklist.notes || "");
  return (
    <div className="space-y-4" data-testid={`precall-checklist-${jobId}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-lg bg-muted/30 border border-border p-3">
        <div className="flex items-center gap-2.5 text-sm">
          <ListChecks className="w-4 h-4 text-primary" />
          <span className="font-semibold">
            {checklist.done} of {checklist.total} confirmed
          </span>
          {checklist.completed_at && (
            <span className="text-[11px] text-muted-foreground font-mono-plex" data-testid={`precall-checklist-completed-at-${jobId}`}>
              · done {new Date(checklist.completed_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex-1 mx-3 h-1.5 rounded-full bg-muted overflow-hidden min-w-[120px]">
          <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${checklist.percent}%` }} />
        </div>
        <span className="text-xs font-mono-plex font-bold text-primary">{checklist.percent}%</span>
      </div>

      <ul className="space-y-1.5" role="list">
        {checklist.items.map((item, i) => (
          <li key={item.id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => onToggle(item.id)}
              className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all border ${
                item.checked
                  ? "bg-success/10 border-success/40 hover:bg-success/15"
                  : "bg-background border-border hover:border-primary/30 hover:bg-muted/30"
              }`}
              data-testid={`precall-check-${jobId}-${item.id}`}
            >
              <span className={`grid place-items-center w-6 h-6 rounded-md shrink-0 mt-0.5 border-2 transition-all ${
                item.checked ? "bg-success border-success text-success-foreground" : "border-muted-foreground/30"
              }`}>
                {item.checked ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : (
                  <span className="text-[10px] font-mono-plex text-muted-foreground">{i + 1}</span>
                )}
              </span>
              <span className={`flex-1 text-sm leading-snug ${item.checked ? "text-foreground line-through decoration-success/60 decoration-1" : ""}`}>
                {item.label}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <div className="space-y-2">
        <label className="label-overline">Notes from the call (optional)</label>
        <textarea
          rows={3}
          value={notesValue}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={() => { if (notesDraft != null && notesDraft !== (checklist.notes || "")) onNotes(notesDraft); }}
          placeholder="e.g. customer is waiting, has 14:00 hard stop. Bringing wife — coffee for two please."
          className="input-default w-full text-sm"
          data-testid={`precall-checklist-notes-${jobId}`}
        />
      </div>

      <button
        type="button"
        onClick={onComplete}
        disabled={busy || !allDone || !!checklist.completed_at}
        className={`btn-primary w-full ${(!allDone || checklist.completed_at) ? "opacity-50 cursor-not-allowed" : ""}`}
        data-testid={`precall-checklist-complete-${jobId}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
        {checklist.completed_at ? "Pre-call complete" : allDone ? "Mark pre-call complete" : `Tick all ${checklist.total} to complete`}
      </button>
    </div>
  );
}


function PreCallRow({ row, selected, onToggleSelected, onChanged }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("sms");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(row.diagnostic_questionnaire);
  const [showAdvQ, setShowAdvQ] = useState(false);
  const [showPlanQuote, setShowPlanQuote] = useState(false);
  const [sentAt, setSentAt] = useState(row.pre_call_sent_at);
  const [checklist, setChecklist] = useState(null);
  const [savingChecklist, setSavingChecklist] = useState(false);

  useEffect(() => {
    if (!open || msg) return;
    api.get(`/jobs/${row.id}/pre-call-message`).then(r => setMsg(r.data)).catch(() => toast.error("Couldn't load message"));
  }, [open, msg, row.id]);

  useEffect(() => {
    if (!open || checklist) return;
    api.get(`/jobs/${row.id}/pre-call-checklist`).then(r => setChecklist(r.data)).catch(() => {});
  }, [open, checklist, row.id]);

  const reload = async () => {
    const r = await api.get(`/jobs/${row.id}/pre-call-message`);
    setMsg(r.data);
  };

  const toggleDiag = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${row.id}/toggle-questionnaire`);
      setDiag(r.data.diagnostic_questionnaire);
      if (msg) await reload();
      toast.success(r.data.diagnostic_questionnaire ? "Questionnaire enabled" : "Questionnaire disabled");
    } catch { toast.error("Couldn't toggle"); }
    finally { setBusy(false); }
  };

  const markSent = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${row.id}/mark-pre-called`, {});
      setSentAt(r.data.pre_call_sent_at);
      toast.success("Marked as pre-called");
      onChanged?.();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const toggleChecklistItem = async (itemId) => {
    if (!checklist || savingChecklist) return;
    const newChecked = { ...Object.fromEntries(checklist.items.map(i => [i.id, i.checked])), [itemId]: !checklist.items.find(i => i.id === itemId)?.checked };
    setSavingChecklist(true);
    try {
      const r = await api.patch(`/jobs/${row.id}/pre-call-checklist`, { checked: newChecked });
      setChecklist(r.data);
    } catch { toast.error("Couldn't save"); }
    finally { setSavingChecklist(false); }
  };

  const saveChecklistNotes = async (notes) => {
    try {
      const r = await api.patch(`/jobs/${row.id}/pre-call-checklist`, { notes });
      setChecklist(r.data);
    } catch { toast.error("Couldn't save notes"); }
  };

  const completeChecklist = async () => {
    if (!checklist) return;
    setSavingChecklist(true);
    try {
      const r = await api.patch(`/jobs/${row.id}/pre-call-checklist`, {
        checked: Object.fromEntries(checklist.items.map(i => [i.id, i.checked])),
        notes: checklist.notes,
        complete: true,
      });
      setChecklist(r.data);
      setSentAt(r.data.completed_at);
      toast.success("Pre-call complete — well done.");
      onChanged?.();
    } catch { toast.error("Couldn't mark complete"); }
    finally { setSavingChecklist(false); }
  };

  const fullSms = msg && msg.questionnaire_path && msg.sms_body
    ? msg.sms_body.replace(msg.questionnaire_path, `${window.location.origin}${msg.questionnaire_path}`)
    : msg?.sms_body || "";
  const fullEmail = msg && msg.questionnaire_path && msg.email_body
    ? msg.email_body.replace(msg.questionnaire_path, `${window.location.origin}${msg.questionnaire_path}`)
    : msg?.email_body || "";

  const vehicle = [row.vehicle_year, row.vehicle_make, row.vehicle_model].filter(Boolean).join(" ");
  const svcKey = row.service_type || "other";
  const svcStyle = SVC_COLOUR[svcKey] || SVC_COLOUR.other;

  return (
    <div className={`border ${sentAt ? "border-border opacity-70" : "border-border"} rounded-lg overflow-hidden`} data-testid={`precall-row-${row.id}`}>
      <div className="flex items-stretch">
        {!sentAt && (
          <label className="flex items-center px-3 cursor-pointer hover:bg-muted/30 border-r border-border" onClick={(e) => e.stopPropagation()} data-testid={`precall-tick-wrap-${row.id}`}>
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelected?.()}
              className="w-4 h-4 accent-primary cursor-pointer"
              data-testid={`precall-tick-${row.id}`}
              aria-label={`Include ${row.customer_name || "this customer"} in bulk send`}
            />
          </label>
        )}
        <button type="button" onClick={() => setOpen(o => !o)} className="flex-1 p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left" data-testid={`precall-toggle-${row.id}`}>
        <div className="font-mono-plex text-sm font-bold w-14 shrink-0">{row.appointment_time || "—"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{row.customer_name || "—"}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${svcStyle}`}>{svcKey}</span>
            {diag && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning/15 text-warning border border-warning/40 inline-flex items-center gap-1">
                <Stethoscope className="w-3 h-3" /> Pre-work form
              </span>
            )}
            {row.questionnaire_submitted && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-success/15 text-success border border-success/40 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Form returned
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {vehicle} · <span className="font-mono-plex">{(row.vehicle_reg || "").toUpperCase()}</span> · {row.work_description || "—"}
          </div>
        </div>
        {sentAt ? (
          <span className="text-[10px] uppercase font-bold tracking-wider text-success bg-success/10 border border-success/30 px-2 py-1 rounded inline-flex items-center gap-1 shrink-0" data-testid={`precall-sent-${row.id}`}>
            <Check className="w-3 h-3" /> Sent
          </span>
        ) : (
          <span className="text-[10px] uppercase font-bold tracking-wider text-warning bg-warning/10 border border-warning/30 px-2 py-1 rounded shrink-0">Pending</span>
        )}
        {open ? <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />}
        </button>
      </div>

      {open && (
        <div className="px-4 pb-4 border-t border-border bg-muted/20 pt-4 space-y-3">
          {/* Contact info + diagnostic toggle */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {row.customer_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> <span className="font-mono-plex">{row.customer_phone}</span></span>}
              {row.customer_email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {row.customer_email}</span>}
            </div>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid={`precall-diag-toggle-${row.id}`}>
              <input type="checkbox" checked={diag} onChange={toggleDiag} disabled={busy} className="rounded border-border accent-warning" />
              <Stethoscope className="w-3.5 h-3.5 text-warning" />
              <span>Diagnostic pre-work questionnaire</span>
            </label>
            <button
              type="button"
              onClick={() => setShowAdvQ(true)}
              className="btn-outline text-xs inline-flex items-center gap-1.5 h-8"
              data-testid={`precall-fill-questionnaire-${row.id}`}
            >
              <Stethoscope className="w-3.5 h-3.5" />
              Fill in with customer
            </button>
            {/* Plan quote — visible on EVERY row. Style shifts when EMaC has
                actively confirmed an opportunity (no live cover found) so the
                gold ones stand out on the diary. Advisors can quote proactively
                without waiting for the background EMaC lookup to complete. */}
            <button
              type="button"
              onClick={() => setShowPlanQuote(true)}
              className={
                row.service_plan_opportunity
                  ? "text-xs font-bold inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-warning/50 bg-warning/10 text-warning hover:bg-warning/20 transition-colors"
                  : "btn-outline text-xs inline-flex items-center gap-1.5 h-8"
              }
              data-testid={
                row.service_plan_opportunity
                  ? `precall-plan-opportunity-${row.id}`
                  : `precall-plan-quote-${row.id}`
              }
              title={
                row.service_plan_opportunity
                  ? "EMaC found no live service plan on this vehicle"
                  : "Send a service plan quote to this customer"
              }
            >
              <Sparkles className="w-3.5 h-3.5" />
              {row.service_plan_opportunity ? "Plan opportunity" : "Quote a plan"}
            </button>
            {(row.service_plan_quotes || []).length > 0 && !row.service_plan_opportunity && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5 h-8" data-testid={`precall-plan-quoted-${row.id}`}>
                Plan quoted {new Date(row.last_service_plan_quote_at).toLocaleDateString("en-GB")}
              </span>
            )}
            {(row.service_plan_quotes || []).length > 0 && row.service_plan_opportunity && (
              <span className="text-xs text-warning inline-flex items-center gap-1.5 h-8" data-testid={`precall-plan-quoted-${row.id}`}>
                Quoted · awaiting response
              </span>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 rounded-lg bg-card border border-border w-fit">
            <button type="button" onClick={() => setTab("checklist")} className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 transition-colors ${tab === "checklist" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid={`precall-tab-checklist-${row.id}`}>
              <CheckSquare className="w-3.5 h-3.5" /> Checklist
              {checklist?.done > 0 && (
                <span className={`text-[10px] font-mono px-1 rounded ${tab === "checklist" ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"}`}>
                  {checklist.done}/{checklist.total}
                </span>
              )}
            </button>
            <button type="button" onClick={() => setTab("sms")} className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 transition-colors ${tab === "sms" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid={`precall-tab-sms-${row.id}`}>
              <MessageSquare className="w-3.5 h-3.5" /> SMS
            </button>
            <button type="button" onClick={() => setTab("email")} className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 transition-colors ${tab === "email" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid={`precall-tab-email-${row.id}`}>
              <Mail className="w-3.5 h-3.5" /> Email
            </button>
          </div>

          {tab === "checklist" ? (
            <PreCallChecklistPanel
              jobId={row.id}
              checklist={checklist}
              busy={savingChecklist}
              onToggle={toggleChecklistItem}
              onNotes={saveChecklistNotes}
              onComplete={completeChecklist}
            />
          ) : !msg ? (
            <div className="py-6 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : tab === "sms" ? (
            <>
              <div className="rounded-lg bg-card border border-border p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`precall-sms-${row.id}`}>{fullSms}</div>
              <div className="flex flex-wrap gap-2">
                <CopyBtn text={fullSms} label="Copy SMS" testid={`precall-copy-sms-${row.id}`} />
                {row.customer_phone && (
                  <a href={`sms:${encodeURIComponent(row.customer_phone)}?&body=${encodeURIComponent(fullSms)}`} className="btn-outline text-xs" data-testid={`precall-open-sms-${row.id}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> Open in Messages
                  </a>
                )}
                <SendLiveButton
                  endpoint={`/jobs/${row.id}/send-pre-call`}
                  channel="sms"
                  disabled={!row.customer_phone}
                  onSent={(r) => { setSentAt(new Date().toISOString()); onChanged?.(); }}
                  testid={`precall-send-sms-live-${row.id}`}
                />
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg bg-card border border-border overflow-hidden">
                <div className="px-3 py-2 border-b border-border text-xs font-semibold">{msg.email_subject}</div>
                <div className="p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`precall-email-${row.id}`}>{fullEmail}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <CopyBtn text={fullEmail} label="Copy email" testid={`precall-copy-email-${row.id}`} />
                {row.customer_email && (
                  <a href={`mailto:${encodeURIComponent(row.customer_email)}?subject=${encodeURIComponent(msg.email_subject)}&body=${encodeURIComponent(fullEmail)}`} className="btn-outline text-xs" data-testid={`precall-open-email-${row.id}`}>
                    <Mail className="w-3.5 h-3.5" /> Open in Mail
                  </a>
                )}
                <SendLiveButton
                  endpoint={`/jobs/${row.id}/send-pre-call`}
                  channel="email"
                  disabled={!row.customer_email}
                  onSent={(r) => { setSentAt(new Date().toISOString()); onChanged?.(); }}
                  testid={`precall-send-email-live-${row.id}`}
                />
              </div>
            </>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-border">
            {diag && msg?.questionnaire_path && (
              <a href={`${window.location.origin}${msg.questionnaire_path}`} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 truncate" data-testid={`precall-q-link-${row.id}`}>
                <ExternalLink className="w-3 h-3" /> <span className="truncate font-mono-plex">{msg.questionnaire_path}</span>
              </a>
            )}
            {!sentAt && (
              <button type="button" onClick={markSent} disabled={busy} className="btn-outline text-xs ml-auto" data-testid={`precall-mark-sent-${row.id}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Mark as sent
              </button>
            )}
          </div>
        </div>
      )}
      <AdvisorQuestionnaireModal
        jobId={row.id}
        open={showAdvQ}
        customerName={row.customer_name}
        onClose={() => setShowAdvQ(false)}
        onSaved={() => { setDiag(true); onChanged?.(); }}
      />
      <ServicePlanQuoteModal
        job={row}
        open={showPlanQuote}
        onClose={() => setShowPlanQuote(false)}
        onSent={() => onChanged?.()}
      />
    </div>
  );
}

// =====================================================================
// Page
// =====================================================================
export default function PreCallsPage() {
  const [date, setDate] = useState(""); // empty → default tomorrow
  const [data, setData] = useState({ count: 0, pending: 0, appointments: [], date: null });
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [selected, setSelected] = useState({}); // job_id -> true/false
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = date ? `/pre-calls?date=${date}` : "/pre-calls";
      const r = await api.get(url);
      setData(r.data);
      // Default-select all PENDING (not yet sent) appointments. Tech can untick.
      const initial = {};
      (r.data.appointments || []).forEach(a => { if (!a.pre_call_sent_at) initial[a.id] = true; });
      setSelected(initial);
      setBulkResult(null);
    } catch { toast.error("Couldn't load pre-calls"); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleSelected = (jobId) => setSelected(p => ({ ...p, [jobId]: !p[jobId] }));
  const selectAll = () => {
    const all = {};
    (data.appointments || []).forEach(a => { if (!a.pre_call_sent_at) all[a.id] = true; });
    setSelected(all);
  };
  const clearAll = () => setSelected({});

  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const sendAll = async () => {
    if (selectedIds.length === 0) { toast.error("Tick at least one customer"); return; }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await api.post("/jobs/bulk-pre-call", {
        job_ids: selectedIds,
        channels: ["sms", "email"],
        skip_already_sent: true,
      });
      setBulkResult(r.data);
      const { sent = 0, skipped = 0, failed = 0 } = r.data;
      const parts = [];
      if (sent) parts.push(`${sent} sent`);
      if (skipped) parts.push(`${skipped} already done`);
      if (failed) parts.push(`${failed} failed`);
      toast.success(parts.join(" · ") || "Done");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk send failed");
    } finally { setBulkBusy(false); }
  };

  const dateLabel = data.date ? new Date(data.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "tomorrow";

  return (
    <div className="min-h-screen bg-background" data-testid="precalls-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="btn-outline" data-testid="precalls-back-btn">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <BrandLink />
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/diary/print${date ? `?date=${date}` : ""}`}
              className="btn-outline"
              data-testid="precalls-print-sheet-btn"
              title="Open the printable advisor run sheet"
            >
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Print sheet</span>
            </Link>
            <button onClick={() => setShowQuick(true)} className="btn-outline" data-testid="precalls-quick-btn" title="Send a pre-call without using the diary — for walk-ins / phone bookings">
              <Sparkles className="w-4 h-4" /> <span className="hidden sm:inline">Quick pre-call</span>
            </button>
            <button onClick={() => setShowImport(true)} className="btn-primary" data-testid="precalls-import-btn">
              <Upload className="w-4 h-4" /> <span className="hidden sm:inline">Import diary</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-overline">Pre-calls</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-2">
              Tomorrow's customers <span className="text-primary">— remind &amp; prep</span>.
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {dateLabel} · {data.count} appointment{data.count === 1 ? "" : "s"} ·
              {" "}{data.pending} pending pre-call{data.pending === 1 ? "" : "s"}
            </p>
          </div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-default text-sm w-fit" data-testid="precalls-date" />
        </div>

        {loading ? (
          <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : data.appointments.length === 0 ? (
          <div className="card-default p-12 text-center" data-testid="precalls-empty">
            <Calendar className="w-10 h-10 mx-auto text-muted-foreground" />
            <div className="font-display font-bold text-xl mt-4">Nothing booked for this day yet.</div>
            <p className="text-sm text-muted-foreground mt-1">Import your diary, send a one-off pre-call, or change the date.</p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
              <button onClick={() => setShowImport(true)} className="btn-primary" data-testid="precalls-empty-import-btn">
                <Upload className="w-4 h-4" /> Import diary
              </button>
              <button onClick={() => setShowQuick(true)} className="btn-outline" data-testid="precalls-empty-quick-btn">
                <Sparkles className="w-4 h-4" /> Quick pre-call
              </button>
            </div>
          </div>
        ) : (
          <>
            {data.pending === 0 && (
              <div className="rounded-lg border border-success/30 bg-success/5 p-4 flex items-center gap-3" data-testid="precalls-all-done">
                <Check className="w-5 h-5 text-success" />
                <div className="text-sm font-semibold">All pre-calls sent — well done.</div>
              </div>
            )}

            {data.pending > 0 && (
              <div className="card-default p-4 bg-gradient-to-br from-primary/5 to-transparent" data-testid="precalls-bulk-bar">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="font-semibold">{selectedIds.length} of {data.pending}</span>
                    <span className="text-muted-foreground">selected · ready to fire SMS + Email</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={selectAll} className="text-xs text-muted-foreground hover:text-foreground" data-testid="precalls-select-all">All</button>
                    <span className="text-muted-foreground text-xs">·</span>
                    <button type="button" onClick={clearAll} className="text-xs text-muted-foreground hover:text-foreground" data-testid="precalls-clear">None</button>
                    <button
                      type="button"
                      onClick={sendAll}
                      disabled={bulkBusy || selectedIds.length === 0}
                      className="btn-primary"
                      data-testid="precalls-send-all-btn"
                    >
                      {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {bulkBusy ? `Sending ${selectedIds.length}…` : `Send all (${selectedIds.length})`}
                    </button>
                  </div>
                </div>
                {bulkBusy && (
                  <p className="text-[11px] text-muted-foreground mt-2">Throttled at ~2/sec — about {Math.ceil(selectedIds.length / 2)}s. SMS will land instantly once your Twilio bundle is approved (currently logged in fallback mode).</p>
                )}
                {bulkResult && !bulkBusy && (
                  <div className="text-xs mt-2 flex flex-wrap items-center gap-3" data-testid="precalls-bulk-result">
                    {bulkResult.sent > 0 && <span className="inline-flex items-center gap-1 text-success"><Check className="w-3 h-3" />{bulkResult.sent} sent</span>}
                    {bulkResult.skipped > 0 && <span className="inline-flex items-center gap-1 text-muted-foreground"><Check className="w-3 h-3" />{bulkResult.skipped} already done</span>}
                    {bulkResult.failed > 0 && <span className="inline-flex items-center gap-1 text-destructive"><AlertTriangle className="w-3 h-3" />{bulkResult.failed} failed</span>}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3" data-testid="precalls-list">
              {data.appointments.map((row) => (
                <PreCallRow
                  key={row.id}
                  row={row}
                  selected={!!selected[row.id]}
                  onToggleSelected={() => toggleSelected(row.id)}
                  onChanged={refresh}
                />
              ))}
            </div>
            <div className="card-default p-4 flex items-start gap-3 text-sm bg-primary/5 border-primary/40">
              <AlertTriangle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span className="text-muted-foreground">Phase B coming: auto-fire at 4pm the day before. For now, tick the rows you want and tap <strong>Send all</strong>. AI-personalised SMS &amp; Email per customer.</span>
            </div>
          </>
        )}
      </main>

      <DiaryImportWizard open={showImport} onClose={() => setShowImport(false)} onImported={refresh} />
      <QuickPreCallModal
        open={showQuick}
        onClose={() => setShowQuick(false)}
        defaultDate={date || undefined}
        onSent={() => refresh()}
      />
    </div>
  );
}
